import * as Yup from "yup";
import {
  TBitValue,
  TReactSetState,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";

import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  CREATE_HELP_CATEGORY,
  GET_HELP_CATEGORY_BY_ID,
  UPDATE_HELP_CATEGORY,
} from "../../services/help-center/HelpCenterService";
import { BIT_VALUE } from "../../utils/AppEnumerations";

export interface IHelpCategoryForm {
  id?: string;
  category_title: string;
  category_description: string;
  display_sequence?: number;
  is_active: TBitValue;
}

export const HELP_CATEGORY_FIELD_INITIAL_VALUES = {
  category_title: "",
  category_description: "",
  display_sequence: undefined,
  is_active: BIT_VALUE.One,
};

export const helpCategoryFieldValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    category_title: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("category_title")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("category_title")],
          ["max", "50"],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("category_title")],
          ["min", "2"],
        ])
      ),

    category_description: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("category_description")],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("category_description")],
          ["min", "2"],
        ])
      )
      .max(
        200,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("category_description")],
          ["max", "200"],
        ])
      ),

    display_sequence: Yup.number()
      .typeError(t("error_message_number"))
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("display_sequence")],
        ])
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min"), [
          ["fieldName", t("display_sequence")],
          ["min", "1"],
        ])
      )
      .max(
        100,
        prepareMessageFromParams(t("error_message_max_value"), [
          ["fieldName", t("display_sequence")],
          ["max", "100"],
        ])
      ),
  });
export const handleSubmitHelpCategory = async (
  values: IHelpCategoryForm,
  setLoading: TReactSetState<boolean>,
  handleClose: (status: boolean) => void,
  id: string | undefined
) => {
  setLoading(true);
  try {
    let helpCategory;
    if (id) {
      helpCategory = await UPDATE_HELP_CATEGORY(values, id);
    } else {
      helpCategory = await CREATE_HELP_CATEGORY(values);
    }
    if (helpCategory && helpCategory.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({
        message: helpCategory.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: helpCategory?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    sweetAlertError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "handleSubmitHelpCategory");
  } finally {
    setLoading(false);
  }
};

export const fetchHelpCategoryFieldById = async (
  id: string,
  handleClose: (status: boolean) => void,
  setHelpCategory: TReactSetState<IHelpCategoryForm | undefined>
) => {
  try {
    const helpCategoryData = await GET_HELP_CATEGORY_BY_ID(id);
    if (
      helpCategoryData &&
      helpCategoryData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setHelpCategory(helpCategoryData.data);
    } else {
      handleClose(false);
      sweetAlertError({
        message: helpCategoryData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchHelpCategoryFieldById");
  }
};
